import { useLookup } from 'hooks';
import dayjs, { Dayjs } from 'dayjs';
import { ACTION_TYPE_ENUM } from 'enums';
import apiService from 'library/api-service';
import { Grid2 as Grid } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { EffortLogItem, User } from 'library/types';
import { GridColDef } from '@mui/x-data-grid-premium';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CustomToolbar, ZebraDataGrid, CustomNoRowsOverlay, DataGridBox } from 'components';
import { renderDataGridLocaleText, renderDatePickerLocaleText, renderFormattedDate } from 'utils';

const EffortTimeline = () => {
  // other hooks
  const intl = useIntl();
  const effortTimelineRequestIdRef = useRef(0);
  const { items: users } = useLookup<User>(apiService.getUsers);

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<EffortLogItem[]>([]);
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(1, 'month'));

  // translate functions
  const getActionTypeTranslated = useCallback(
    (actionType: ACTION_TYPE_ENUM) =>
      ({
        [ACTION_TYPE_ENUM.INSERT]: intl.formatMessage({ id: 'enum.actionType.insert', defaultMessage: 'Ekleme' }),
        [ACTION_TYPE_ENUM.DELETE]: intl.formatMessage({ id: 'enum.actionType.delete', defaultMessage: 'Silme' }),
        [ACTION_TYPE_ENUM.UPDATE]: intl.formatMessage({ id: 'enum.actionType.update', defaultMessage: 'Güncelleme' }),
        [ACTION_TYPE_ENUM.SIGN_IN]: intl.formatMessage({ id: 'enum.actionType.signIn', defaultMessage: 'Giriş' }),
        [ACTION_TYPE_ENUM.RESTORE]: intl.formatMessage({ id: 'enum.actionType.restore', defaultMessage: 'Yenileme' }),
        [ACTION_TYPE_ENUM.ASSIGN]: intl.formatMessage({ id: 'enum.actionType.assign', defaultMessage: 'Atama' }),
        [ACTION_TYPE_ENUM.SEND_EMAIL]: intl.formatMessage({
          id: 'enum.actionType.sendEmail',
          defaultMessage: 'Hatırlatma E-Postası',
        }),
      })[actionType],
    [intl]
  );

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'createdAt',
        headerName: intl.formatMessage({
          id: 'timeline.createdAt',
          defaultMessage: 'Tarih',
        }),
        align: 'center',
        headerAlign: 'center',
        type: 'date',
        valueGetter: (value: string) => new Date(value),
        renderCell: (params) => renderFormattedDate(params.value, true),
        flex: 0.25,
      },
      {
        field: 'userId',
        headerName: intl.formatMessage({ id: 'timeline.user', defaultMessage: 'Kullanıcı' }),
        valueGetter: (value: number, row) => {
          if (!value) return row.customerNameSurname;

          const user = users.find((user) => value === user.id);
          return user?.nameSurname ?? '';
        },
        flex: 0.25,
      },
      {
        field: 'action',
        headerName: intl.formatMessage({ id: 'timeline.action', defaultMessage: 'Eylem' }),
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value: number) => getActionTypeTranslated(value),
        type: 'singleSelect',
        valueOptions: () =>
          Object.values(ACTION_TYPE_ENUM)
            .filter((v) => typeof v === 'number')
            .map((v) => getActionTypeTranslated(v as ACTION_TYPE_ENUM)),
        flex: 0.25,
      },
      {
        field: 'trackNo',
        headerName: intl.formatMessage({ id: 'effortTracing.uniqueKey', defaultMessage: 'Efor Takip No' }),
        headerAlign: 'center',
        align: 'center',
        flex: 0.25,
      },
      {
        field: 'explanation',
        headerName: intl.formatMessage({ id: 'timeline.explanation', defaultMessage: 'Açıklama' }),
        flex: 1,
      },
    ],
    [intl, users, getActionTypeTranslated]
  );

  // async helper functions
  const getValues = useCallback(async () => {
    const requestId = ++effortTimelineRequestIdRef.current;

    try {
      setLoading(true);

      const response = await apiService.getEffortTimelineList(startDate, endDate);

      if (requestId === effortTimelineRequestIdRef.current && response.isSuccess) {
        const duplicateArr = [...response.data];
        const sortedData = duplicateArr.sort((a, b) =>
          new Date(b.createdAt).getTime() > new Date(a.createdAt).getTime() ? 1 : -1
        );

        setData(sortedData);
      }
    } catch {
    } finally {
      if (requestId === effortTimelineRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [startDate, endDate]);

  // useEffect hooks
  useEffect(() => {
    getValues();

    return () => {
      effortTimelineRequestIdRef.current += 1;
    };
  }, [getValues]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <DatePicker
            label={<FormattedMessage id="timeline.startDate" defaultMessage="Başlangıç Tarihi" />}
            localeText={renderDatePickerLocaleText(intl)}
            disableFuture
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: 'outlined',
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <DatePicker
            label={<FormattedMessage id="timeline.endDate" defaultMessage="Bitiş Tarihi" />}
            localeText={renderDatePickerLocaleText(intl)}
            disableFuture
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: 'outlined',
              },
            }}
          />
        </Grid>
      </Grid>
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => new Date(row.createdAt).getTime() + Number(row.trackNo)}
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          rows={data}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default EffortTimeline;
